import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sparkles, ChevronLeft, ChevronRight, Check, X } from "lucide-react";
import * as api from "./api";
import type { Campaign } from "./api";
import { SectionCard, RadioCards, ChipMultiSelect, formatPhone } from "../me-ui";

// Campaign Wizard (CC-S2/D68) — a guided 5-step New-Campaign flow that is a thin
// wrapper over the CC-S1 editor's existing save paths (saveCampaign / saveAgents /
// saveNumbers / assignByList / applyAutoAssign). No new backend. Predictive stays
// disabled (D35). The full editor remains for power edits + clone/recycle.

const emptyCampaign: Partial<Campaign> = {
  name: "", status: "draft", dialing_mode: "preview", call_order: "adaptive",
  max_attempts_per_record: 3, retry_value: 3, retry_unit: "hour", call_recording: true,
  suppress_federal_dnc: false, suppress_system_dnc: false, suppress_wireless: false, suppress_litigator: false,
  calling_hours_start: "08:00", calling_hours_end: "21:00", timezone: "America/New_York",
  call_result_set: ["Answering Machine", "Do Not Call", "No Answer", "Successful Sale", "Voicemail"],
  agent_script_id: null, auto_assign_criteria: null, auto_assign_enabled: true, smart_local_presence: false,
};

const STEPS = ["Dialing Mode", "Agents", "Contacts", "Phone Numbers", "Call Results", "Review & Save"];

// editable chip list (Call Results) — same behaviour as the editor's
const ChipsEditor = ({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) => {
  const [text, setText] = useState("");
  const add = () => { const t = text.trim(); if (t && !value.includes(t)) onChange([...value, t]); setText(""); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {v}<button onClick={() => onChange(value.filter((x) => x !== v))} className="text-muted-foreground hover:text-red-600"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Add a call result…" className="h-8 w-56" />
        <Button size="sm" variant="outline" onClick={add} disabled={!text.trim()}>Add</Button>
      </div>
    </div>
  );
};

export default function CampaignWizard({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const notify = useNotify();
  const [step, setStep] = useState(0);
  const [c, setC] = useState<Partial<Campaign>>(emptyCampaign);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [numberIds, setNumberIds] = useState<string[]>([]);
  const [opts, setOpts] = useState<any>({ users: [], phones: [], scripts: [], lists: [] });
  const [crit, setCrit] = useState<Record<string, string>>({});
  const [assignList, setAssignList] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.loadOptions().then(setOpts); }, []);

  const set = (k: keyof Campaign, v: any) => setC((p) => ({ ...p, [k]: v }));
  const toggle = (arr: string[], setArr: (v: string[]) => void, val: string) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const nameOf = (list: any[], idv: string | null, key = "id", label = "name") => list.find((x) => x[key] === idv)?.[label];
  const critEntries = Object.entries(crit).filter(([, v]) => v && String(v).trim());

  const finish = async (asDraft: boolean) => {
    if (!c.name?.trim()) { setStep(5); return notify("Give the campaign a name to finish", { type: "error" }); }
    setSaving(true);
    const criteria = Object.fromEntries(critEntries);
    const payload: any = {
      ...c,
      status: asDraft ? "draft" : "active",
      auto_assign_criteria: Object.keys(criteria).length ? criteria : null,
    };
    const { data, error } = await api.saveCampaign(payload);
    if (error) { setSaving(false); return notify(`Save failed: ${error.message}`, { type: "error" }); }
    const cid = (data as any)?.id as string;
    await api.saveAgents(cid, agentIds);
    await api.saveNumbers(cid, numberIds);
    if (assignList) { const r = await api.assignByList(cid, assignList); if (!r.error) notify(`Assigned ${r.data ?? 0} leads from the list`, { type: "info" }); }
    if (Object.keys(criteria).length && (c.auto_assign_enabled ?? true)) await api.applyAutoAssign(cid);
    setSaving(false);
    notify(asDraft ? "Campaign saved as draft (won't dial until activated)" : "Campaign created", { type: "success" });
    onDone();
  };

  const pct = Math.round(((step + 1) / STEPS.length) * 100);
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="max-w-3xl space-y-4">
      {/* header + progress */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-orange-500" />
          <h2 className="text-lg font-semibold">New Campaign — Guided Setup</h2>
          <span className="ml-auto text-sm text-muted-foreground">Step {step + 1} of {STEPS.length} · {STEPS[step]}</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {STEPS.map((label, i) => (
            <button key={label} onClick={() => setStep(i)}
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${i === step ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-300" : i < step ? "text-green-700 dark:text-green-400 border-green-300" : "text-muted-foreground"}`}>
              {i < step && <Check className="inline h-2.5 w-2.5 mr-0.5" />}{label}
            </button>
          ))}
        </div>
      </div>

      {/* step body */}
      {step === 0 && (
        <SectionCard title="Dialing Mode" sub="How agents place calls in this campaign.">
          <RadioCards cols={2} value={c.dialing_mode ?? "preview"} onChange={(v) => set("dialing_mode", v)}
            options={[
              { value: "preview", label: "Preview Dialer", desc: "Agent reviews each contact, then clicks to dial. Manual (non-ATDS)." },
              { value: "predictive", label: "Predictive Dialer", desc: "Auto-dials multiple lines.", disabled: true, note: "Gated off (D35)." },
            ]} />
        </SectionCard>
      )}

      {step === 1 && (
        <SectionCard title="Agents" sub={`${agentIds.length} selected · who will dial this campaign`}>
          <ChipMultiSelect
            options={opts.users.map((u: any) => ({ value: u.id, label: u.full_name, sub: u.email }))}
            selected={agentIds} onToggle={(v) => toggle(agentIds, setAgentIds, v)} empty="No active users." />
        </SectionCard>
      )}

      {step === 2 && (
        <SectionCard title="Contacts" sub="Which raw leads this campaign works — a list and/or auto-criteria.">
          <div className="space-y-1"><Label className="text-xs">Assign a whole list (optional)</Label>
            <Select value={assignList} onValueChange={setAssignList}>
              <SelectTrigger><SelectValue placeholder="Pick a list…" /></SelectTrigger>
              <SelectContent>{opts.lists.map((l: any) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-md border bg-muted/30 p-3 space-y-2 mt-3">
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">Auto-assign new matching leads</div><div className="text-xs text-muted-foreground">New arrivals matching the criteria join automatically.</div></div>
              <Switch checked={c.auto_assign_enabled ?? true} onCheckedChange={(v) => set("auto_assign_enabled", v)} />
            </div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Auto-assign criteria (all set fields must match)</Label>
            <div className="grid grid-cols-4 gap-2">
              {(["case_type", "county", "source", "list"] as const).map((f) => (
                <div key={f} className="space-y-1"><Label className="text-[10px] uppercase text-muted-foreground">{f}</Label>
                  <Input value={crit[f] ?? ""} onChange={(e) => setCrit((p) => ({ ...p, [f]: e.target.value }))} className="h-8" placeholder={f === "list" ? "list id" : ""} />
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      )}

      {step === 3 && (
        <SectionCard title="Phone Numbers" sub={`${numberIds.length} selected · caller-ID numbers this campaign dials from`}>
          <ChipMultiSelect
            options={opts.phones.map((p: any) => ({ value: p.id, label: formatPhone(p.e164), sub: p.purpose }))}
            selected={numberIds} onToggle={(v) => toggle(numberIds, setNumberIds, v)} empty="No numbers configured." />
          <p className="text-xs text-muted-foreground mt-2">Add numbers (or buy new) from the full editor. Smart Local Presence needs ≥2 numbers.</p>
        </SectionCard>
      )}

      {step === 4 && (
        <SectionCard title="Call Results" sub="The one-click disposition chips agents use on each call.">
          <ChipsEditor value={c.call_result_set ?? []} onChange={(v) => set("call_result_set", v)} />
        </SectionCard>
      )}

      {step === 5 && (
        <SectionCard title="Review & Save">
          <div className="space-y-1"><Label className="text-xs">Campaign name</Label>
            <Input value={c.name ?? ""} onChange={(e) => set("name", e.target.value)} placeholder="e.g. FTM — Probate — 202507" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Dialing Mode</span><span className="font-semibold">{c.dialing_mode}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Agents</span><span className="font-semibold">{agentIds.length ? agentIds.map((a) => nameOf(opts.users, a, "id", "full_name")).filter(Boolean).join(", ") : "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Phone Numbers</span><span className="font-semibold">{numberIds.length || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">List</span><span className="font-semibold">{assignList ? (nameOf(opts.lists, assignList, "id", "title") ?? "1") : "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Criteria</span><span className="font-semibold">{critEntries.length ? critEntries.map(([k, v]) => `${k}=${v}`).join(", ") : "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Call Results</span><span className="font-semibold">{(c.call_result_set ?? []).length} set</span></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Save as Draft holds it (won't dial); Save Campaign activates it. You can fine-tune everything later in the full editor.</p>
        </SectionCard>
      )}

      {/* nav */}
      <div className="flex items-center gap-2 border-t pt-3">
        <Button variant="ghost" onClick={onCancel}><X className="mr-1 h-4 w-4" />Cancel</Button>
        <div className="flex-1" />
        {step > 0 && <Button variant="outline" onClick={prev}><ChevronLeft className="mr-1 h-4 w-4" />Back</Button>}
        {step < STEPS.length - 1
          ? <Button onClick={next}>Next<ChevronRight className="ml-1 h-4 w-4" /></Button>
          : <>
              <Button variant="outline" onClick={() => finish(true)} disabled={saving}>{saving ? "Saving…" : "Save as Draft"}</Button>
              <Button onClick={() => finish(false)} disabled={saving}><Check className="mr-1 h-4 w-4" />{saving ? "Saving…" : "Save Campaign"}</Button>
            </>}
      </div>
    </div>
  );
}
